export interface BusinessType {
  value: string;
  label: string;
  emoji: string;
  description: string;
  supportedActivityTypes: ('animal' | 'adventure' | 'both')[];
}

/**
 * Comprehensive business types with activity type support
 * Each business type specifies which activity types it typically supports
 */
export const BUSINESS_TYPES: BusinessType[] = [
  // Animal-focused businesses
  {
    value: 'wildlife_sanctuary',
    label: 'Wildlife Sanctuary',
    emoji: '🦁',
    description: 'Protected areas for wildlife conservation and rehabilitation',
    supportedActivityTypes: ['animal']
  },
  {
    value: 'wildlife_park',
    label: 'Wildlife Park',
    emoji: '🦒',
    description: 'Parks dedicated to wildlife observation and education',
    supportedActivityTypes: ['animal']
  },
  {
    value: 'conservation_center',
    label: 'Conservation Center',
    emoji: '🌱',
    description: 'Centers focused on wildlife research and conservation',
    supportedActivityTypes: ['animal']
  },
  {
    value: 'safari_lodge',
    label: 'Safari Lodge',
    emoji: '🏕️',
    description: 'Accommodation specializing in wildlife safari experiences',
    supportedActivityTypes: ['animal', 'both']
  },

  // Adventure-focused businesses
  {
    value: 'adventure_company',
    label: 'Adventure Company',
    emoji: '🏔️',
    description: 'Companies specializing in outdoor adventure activities',
    supportedActivityTypes: ['adventure']
  },
  {
    value: 'eco_lodge',
    label: 'Eco Lodge',
    emoji: '🌿',
    description: 'Sustainable lodges offering nature-based experiences',
    supportedActivityTypes: ['adventure', 'both']
  },

  // Universal businesses (support both)
  {
    value: 'tour_operator',
    label: 'Tour Operator',
    emoji: '🌍',
    description: 'Full-service tour companies offering diverse experiences',
    supportedActivityTypes: ['animal', 'adventure', 'both']
  },
  {
    value: 'nature_center',
    label: 'Nature Center',
    emoji: '🌳',
    description: 'Educational centers combining wildlife and outdoor activities',
    supportedActivityTypes: ['animal', 'adventure', 'both']
  },
  {
    value: 'outdoor_education',
    label: 'Outdoor Education',
    emoji: '📚',
    description: 'Educational programs in natural environments',
    supportedActivityTypes: ['animal', 'adventure', 'both']
  },

  // Catch-all
  {
    value: 'other',
    label: 'Other',
    emoji: '🏢',
    description: 'Other wildlife or adventure-related businesses',
    supportedActivityTypes: ['animal', 'adventure', 'both']
  }
];

/**
 * Helper function to filter business types based on selected activity types
 */
export const getBusinessTypesByActivityTypes = (
  hasAnimalActivities: boolean,
  hasAdventureActivities: boolean
): BusinessType[] => {
  // If neither activity type is selected, return empty array
  if (!hasAnimalActivities && !hasAdventureActivities) {
    return [];
  }

  return BUSINESS_TYPES.filter(businessType => {
    // Check if the business type supports the selected activity types
    if (hasAnimalActivities && hasAdventureActivities) {
      // Both activity types selected - show businesses that support both or either
      return businessType.supportedActivityTypes.includes('both') ||
             businessType.supportedActivityTypes.includes('animal') ||
             businessType.supportedActivityTypes.includes('adventure');
    } else if (hasAnimalActivities) {
      // Only animal activities selected
      return businessType.supportedActivityTypes.includes('animal') ||
             businessType.supportedActivityTypes.includes('both');
    } else if (hasAdventureActivities) {
      // Only adventure activities selected  
      return businessType.supportedActivityTypes.includes('adventure') ||
             businessType.supportedActivityTypes.includes('both');
    }
    
    return false;
  });
};

/**
 * Helper function to get business type by value
 */
export const getBusinessTypeByValue = (value: string): BusinessType | undefined => {
  return BUSINESS_TYPES.find(type => type.value === value);
};

/**
 * Helper function to get suggested business types based on activity selection
 * Returns the most relevant business types first
 */
export const getSuggestedBusinessTypes = (
  hasAnimalActivities: boolean,
  hasAdventureActivities: boolean
): BusinessType[] => {
  const filteredTypes = getBusinessTypesByActivityTypes(hasAnimalActivities, hasAdventureActivities);
  
  // Sort by relevance - 'both' types first if both activities selected
  if (hasAnimalActivities && hasAdventureActivities) {
    return filteredTypes.sort((a, b) => {
      const aSupportsBoth = a.supportedActivityTypes.includes('both');
      const bSupportsBoth = b.supportedActivityTypes.includes('both');
      
      if (aSupportsBoth && !bSupportsBoth) return -1;
      if (!aSupportsBoth && bSupportsBoth) return 1;
      return 0;
    });
  }
  
  return filteredTypes;
}; 